import React, { useContext, useState, useEffect } from 'react';
import { ToolTip } from '@trp-beacon-ui-kit/react-communication';
import {
  InputField,
  SelectField,
  RadioGroupField
} from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import styles from './styles.css';
import Heading from '../../components/Typography/Heading/Heading';
import plusIcon from '../../assets/images/plus-icon.svg';
import {
  suffixList,
  relationshipList,
  beneficiaryTypes,
  beneficiaryCategory,
  formatBeneficiariesForList,
  getDisplayName,
  getAllocationTotal,
  formatBeneficiariesForApp
} from './util';
import { withDefault } from '../../core/util';
import {
  formatSsnForDisplay,
  handleSpecialInputFormat
} from '../../flows/shared/utils';
import {
  firstnameValidation,
  middlenameValidation,
  lastnameValidation
} from '../../flows/shared/validations';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import {
  generateBeneficiaryTestID,
  generateGeneralTestID
} from '../../generateAutomationTestIDs';

const BeneficiariesScreen = () => {
  const { back, next, formProps, appState } = useContext(FlowContext);
  const {
    store: {
      app: { beneficiaries },
      incompleteBeneficiaries,
      loadedData = {}
    }
  } = appState;
  const { existingBeneficiaries } = loadedData;

  const tempBenes = { individuals: [], entities: [] };

  const parsedBenes = formatBeneficiariesForList(
    incompleteBeneficiaries || beneficiaries || tempBenes
  );
  const [primaryBenes, setPrimaryBenes] = useState(parsedBenes.primary || []);
  const [secondaryBenes, setSecondaryBenes] = useState(
    parsedBenes.secondary || []
  );
  const [arePrimaryBenes, setArePrimaryBenes] = useState(
    primaryBenes.length > 0
  );
  const [meetsAllocation, setMeetsAllocation] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [dobValues, setDobValues] = useState({});
  const [ssnValues, setSSNValues] = useState({});
  const [entitySSNValues, setEntitySSNValues] = useState({});

  useEffect(() => {
    setArePrimaryBenes(primaryBenes.length > 0);
  }, [primaryBenes]);

  useEffect(() => {
    setMeetsAllocation(
      getAllocationTotal(primaryBenes) === 100 &&
        getAllocationTotal(secondaryBenes) === 100
    );
  }, [primaryBenes, secondaryBenes]);

  const getBeneForSubmission = () => {
    return formatBeneficiariesForApp([...primaryBenes, ...secondaryBenes]);
  };

  const beneficiariesExist = () => {
    return primaryBenes.length !== 0 || secondaryBenes.length !== 0;
  };

  const getSubmitDisabled = () => {
    // if user is removing prev entered Beneficiaries
    if (!beneficiariesExist() && parsedBenes.primary.length > 0) {
      return false;
    }
    if (!meetsAllocation || !beneficiariesExist()) {
      return true;
    }
    return false;
  };

  const getShowSkipModal = showModal =>
    showModal ? 'dangerouslySkipValidation:open-modal' : 'skip';

  /* eslint-disable react/no-unstable-nested-components */
  const BeneficiaryList = (benes, setBenes, type, disabled) => {
    const setBeneAllocations = updatedBenes => {
      const temp = updatedBenes;
      if (temp.length === 3) {
        temp[0].percentage = 33;
        temp[1].percentage = 33;
        temp[2].percentage = 34;
      } else {
        for (let i = 0; i < temp.length; i++) {
          temp[i].percentage = 100 / temp.length;
        }
      }
      setBenes([...temp]);
    };

    const addBene = () => {
      const newBenes = [
        ...benes,
        {
          taxID: '',
          name: {
            firstName: '',
            middleName: '',
            lastName: '',
            suffix: ''
          },
          relationship: '',
          entityName: '',
          percentage: '',
          category: beneficiaryCategory.PERSON,
          beneficiaryType: type
        }
      ];
      setShowSkipModal(true);
      setBeneAllocations(newBenes);
    };

    const removeBene = idx => {
      setBeneAllocations(benes.filter((_, index) => index !== idx));
    };

    const filterByName = (bene, matchName) => {
      const noSpacesMatchName = matchName.replace(/\s+/g, '');
      const noSpacesBeneName = `${bene.name.firstName}${bene.name.middleName}${bene.name.lastName}`.replace(
        /\s+/g,
        ''
      );
      return noSpacesBeneName === noSpacesMatchName;
    };

    /* eslint-disable  no-param-reassign */
    const handleChange = (event, idx, property) => {
      benes[idx][property] =
        property === 'percentage'
          ? parseInt(event.target.value, 10)
          : event.target.value;
      setBenes([...benes]);
    };

    const handleNameChange = (event, idx, property) => {
      benes[idx].name[property] = event.target.value;
      setBenes([...benes]);
    };

    const handleExistingBeneSelection = (beneCategory, index) => event => {
      const selectedExistingBene =
        beneCategory === beneficiaryCategory.PERSON
          ? existingBeneficiaries.individuals.find(b =>
              filterByName(b, event?.target?.value)
            )
          : existingBeneficiaries.entities.find(
              b => b.name === event?.target?.value
            );
      // update bene data to include selected existing bene
      benes[index] = {
        taxID: withDefault(selectedExistingBene.taxID, ''),
        dateOfBirth: withDefault(selectedExistingBene.dateOfBirth, ''),

        relationship: withDefault(selectedExistingBene.relationship, 'OTHER'),
        percentage: withDefault(
          parseInt(selectedExistingBene.percentage, 10),
          0
        ),
        category: beneCategory,
        beneficiaryType: type
      };

      if (beneCategory === beneficiaryCategory.PERSON) {
        benes[index].name = {
          firstName: withDefault(selectedExistingBene.name.firstName, ''),
          middleName: withDefault(selectedExistingBene.name.middleName, ''),
          lastName: withDefault(selectedExistingBene.name.lastName, ''),
          suffix: withDefault(selectedExistingBene.name.suffix, '')
        };
        benes[index].entityName = '';
      } else {
        benes[index].name = {
          firstName: '',
          middleName: '',
          lastName: '',
          suffix: ''
        };
        benes[index].entityName = withDefault(selectedExistingBene.name, '');
      }

      setBenes([...benes]);
      setBeneAllocations(benes);
    };

    const beneAlreadySelected = (existingBeneName, beneCategory) => {
      if (beneCategory === beneficiaryCategory.PERSON) {
        const existingPersonAlreadyAdded =
          primaryBenes.filter(b => filterByName(b, existingBeneName))?.length ||
          secondaryBenes.filter(b => filterByName(b, existingBeneName))?.length;

        return existingPersonAlreadyAdded;
      }
      const existingEntityAlreadyAdded =
        primaryBenes.filter(b => b.entityName === existingBeneName)?.length ||
        secondaryBenes.filter(b => b.entityName === existingBeneName)?.length;

      return existingEntityAlreadyAdded;
    };

    /* eslint-enable no-param-reassign */
    const beneficiaryTotals = () => {
      return (
        <>
          {benes.length > 0 && getDisplayName(benes[0]) && (
            <div className="bene-list-container">
              {benes.map((bene, idx) => {
                return (
                  <>
                    {getDisplayName(bene) !== '' && (
                      <div className="bene-list-object">
                        <div className="bene-list-name blocked_data">
                          {getDisplayName(bene)}
                        </div>
                        <div className="bene-list-input">
                          <InputField
                            title=""
                            {...formProps.percentage}
                            name={`${formProps.percentage?.name}-${type}-${idx}`}
                            data-name={generateBeneficiaryTestID(
                              'BeneficiaryPercentage',
                              type,
                              idx
                            )}
                            value={bene.percentage}
                            onChange={e => handleChange(e, idx, 'percentage')}
                          />{' '}
                          %
                        </div>
                      </div>
                    )}
                  </>
                );
              })}
              <div className="bene-total-container">
                <div>Total for {type.toLowerCase()} beneficiaries: </div>
                <div>{getAllocationTotal(benes)}%</div>
              </div>
              {getAllocationTotal(benes) !== 100 && (
                <div role="alert" aria-atomic="true" className="error-message">
                  Total allocation must be 100%. Please revise allocation.
                </div>
              )}
            </div>
          )}
          <style jsx>{styles}</style>
        </>
      );
    };

    /* eslint-disable complexity */
    // @heather - fix complexity
    return (
      <>
        {benes.map((bene, idx) => {
          const PERSON_CATEGORY = beneficiaryCategory.PERSON;
          const ENTITY_CATEGORY = beneficiaryCategory.ENTITY;
          return (
            <div key={bene} className="bene-object-container">
              <div className="bene-type">
                <RadioGroupField
                  {...formProps.beneficiaryType}
                  title="Beneficiary Type"
                  defaultValue={PERSON_CATEGORY}
                  onChange={event => handleChange(event, idx, 'category')}
                  options={[
                    {
                      title: 'Person',
                      value: PERSON_CATEGORY,
                      defaultChecked: bene.category === PERSON_CATEGORY
                    },
                    {
                      title: 'Entity',
                      value: ENTITY_CATEGORY,
                      defaultChecked: bene.category === ENTITY_CATEGORY
                    }
                  ]}
                  name={`beneficiaryType-${type}`}
                />
              </div>
              {bene.category === PERSON_CATEGORY && (
                <>
                  {existingBeneficiaries &&
                    existingBeneficiaries.individuals?.length > 0 && (
                      <div className="bene-dropdown-container">
                        <SelectField
                          title="Current Beneficiaries"
                          type="select"
                          id={`${bene.name.firstName}${Math.random}`}
                          name={`${bene.name.firstName}${Math.random}`}
                          data-name={generateBeneficiaryTestID(
                            'ExistingBeneficiaryName',
                            PERSON_CATEGORY,
                            idx
                          )}
                          className="blocked_data"
                          onChange={handleExistingBeneSelection(
                            bene.category,
                            idx
                          )}>
                          <option hidden value="">
                            Beneficiaries
                          </option>
                          {existingBeneficiaries.individuals?.map(
                            existingBene => {
                              const beneFullName = `${existingBene.name.firstName} ${existingBene.name.middleName} ${existingBene.name.lastName}`;
                              return (
                                <option
                                  key={`${existingBene.name.firstName}${Math.random}`}
                                  value={beneFullName}
                                  data-name={beneFullName}
                                  className="blocked_data"
                                  disabled={beneAlreadySelected(
                                    beneFullName,
                                    bene.category
                                  )}>
                                  {beneFullName}
                                </option>
                              );
                            }
                          )}
                        </SelectField>
                      </div>
                    )}
                  <div className="person-category">
                    <div className="name-fields">
                      <div className="first-name">
                        <InputField
                          title="First Name"
                          {...formProps.beneFirstName}
                          name={`${formProps.beneFirstName?.name}-${type}-${idx}`}
                          data-name={generateBeneficiaryTestID(
                            'BeneficiaryFirstName',
                            PERSON_CATEGORY,
                            idx
                          )}
                          onChange={event =>
                            handleNameChange(event, idx, 'firstName')
                          }
                          defaultValue={bene.name.firstName}
                          validation={firstnameValidation}
                          className="blocked_data"
                        />
                      </div>
                      <div className="middle-name">
                        <InputField
                          title="Middle Name"
                          {...formProps.beneMiddleName}
                          name={`${formProps.beneMiddleName?.name}-${type}-${idx}`}
                          data-name={generateBeneficiaryTestID(
                            'BeneficiaryMiddleName',
                            PERSON_CATEGORY,
                            idx
                          )}
                          defaultValue={bene.name.middleName}
                          onChange={event =>
                            handleNameChange(event, idx, 'middleName')
                          }
                          validation={middlenameValidation}
                          className="blocked_data"
                        />
                      </div>
                      <div className="last-name">
                        <InputField
                          title="Last Name"
                          {...formProps.beneLastName}
                          name={`${formProps.beneLastName?.name}-${type}-${idx}`}
                          data-name={generateBeneficiaryTestID(
                            'BeneficiaryLastName',
                            PERSON_CATEGORY,
                            idx
                          )}
                          defaultValue={bene.name.lastName}
                          onChange={event =>
                            handleNameChange(event, idx, 'lastName')
                          }
                          validation={lastnameValidation}
                          className="blocked_data"
                        />
                      </div>
                    </div>
                    <div className="personal-info-fields">
                      <div className="suffix-input">
                        <SelectField
                          title="Suffix"
                          {...formProps.beneSuffix}
                          name={`${formProps.beneSuffix?.name}-${type}-${idx}`}
                          data-name={generateBeneficiaryTestID(
                            'BeneficiarySuffix',
                            PERSON_CATEGORY,
                            idx
                          )}
                          onChange={event =>
                            handleNameChange(event, idx, 'suffix')
                          }>
                          <option hidden value="">
                            Suffix
                          </option>
                          {suffixList.map(abrr => {
                            return (
                              <option
                                key={abrr.text}
                                value={abrr.value}
                                data-name={abrr.value}
                                selected={bene.name.suffix === abrr.value}>
                                {abrr.text}
                              </option>
                            );
                          })}
                        </SelectField>
                      </div>
                      <div className="relationship-input">
                        <SelectField
                          title="Relationship"
                          {...formProps.beneRelationship}
                          name={`${formProps.beneRelationship?.name}-${type}-${idx}`}
                          data-name={generateBeneficiaryTestID(
                            'BeneficiaryRelationship',
                            PERSON_CATEGORY,
                            idx
                          )}
                          onChange={event =>
                            handleChange(event, idx, 'relationship')
                          }>
                          <option hidden value="">
                            Relationship
                          </option>
                          {relationshipList.map(abrr => {
                            return (
                              <option
                                key={abrr.text}
                                value={abrr.value}
                                data-name={abrr.value}
                                selected={bene.relationship === abrr.value}>
                                {abrr.text}
                              </option>
                            );
                          })}
                        </SelectField>
                      </div>
                      <div className="dob-input">
                        <InputField
                          style={{ paddingRight: '8px' }}
                          type="text"
                          title="Date of Birth"
                          aria-describedby="hint"
                          {...formProps.beneDateOfBirth}
                          name={`${formProps.beneDateOfBirth?.name}-${type}-${idx}`}
                          data-name={generateBeneficiaryTestID(
                            'BeneficiaryBirthDate',
                            PERSON_CATEGORY,
                            idx
                          )}
                          value={withDefault(
                            dobValues[
                              `${formProps.beneDateOfBirth?.name}-${type}-${idx}`
                            ],
                            bene.dateOfBirth
                          )}
                          onChange={event => {
                            handleChange(event, idx, 'dateOfBirth');
                            handleSpecialInputFormat(
                              event,
                              'DATE',
                              setDobValues,
                              `${formProps.beneDateOfBirth?.name}-${type}-${idx}`
                            );
                          }}
                          maxLength="10"
                          placeholder="mm/dd/yyyy"
                          className="blocked_data"
                        />
                        <dd id="hint" style={{ display: 'none' }}>
                          This is a required field. Please enter a two digit
                          month, a two digit day and a four digit year for the
                          Date of Birth value.
                        </dd>
                      </div>
                      <div className="ssn-input">
                        <InputField
                          title="SSN"
                          placeholder="000-00-0000"
                          maxLength="11"
                          {...formProps.beneTaxID}
                          name={`${formProps.beneTaxID?.name}-${type}-${idx}`}
                          data-name={generateBeneficiaryTestID(
                            'BeneficiaryTaxId',
                            PERSON_CATEGORY,
                            idx
                          )}
                          value={withDefault(
                            ssnValues[
                              `${formProps.beneTaxID?.name}-${type}-${idx}`
                            ],
                            formatSsnForDisplay(bene.taxID)
                          )}
                          onChange={event => {
                            handleChange(event, idx, 'taxID');
                            handleSpecialInputFormat(
                              event,
                              'SSN',
                              setSSNValues,
                              `${formProps.beneTaxID?.name}-${type}-${idx}`
                            );
                          }}
                          className="blocked_data"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              {bene.category === ENTITY_CATEGORY && (
                <>
                  {existingBeneficiaries &&
                    existingBeneficiaries.entities?.length > 0 && (
                      <div className="bene-dropdown-container">
                        <SelectField
                          {...formProps.entityName}
                          title="Current Beneficiaries"
                          type="select"
                          id={bene.entityName}
                          name={bene.entityName}
                          data-name={generateGeneralTestID(
                            'Beneficiary',
                            'ExistingEntityName'
                          )}
                          onChange={handleExistingBeneSelection(
                            bene.category,
                            idx
                          )}>
                          <option hidden value="">
                            Beneficiaries
                          </option>
                          {existingBeneficiaries.entities?.map(existingBene => {
                            return (
                              <option
                                key={existingBene.name}
                                value={existingBene.name}
                                data-name={existingBene.name}
                                disabled={beneAlreadySelected(
                                  existingBene.name,
                                  bene.category
                                )}>
                                {existingBene.name}
                              </option>
                            );
                          })}
                        </SelectField>
                      </div>
                    )}
                  <div className="person-category">
                    <div className="personal-info-fields">
                      <div className="entity-name">
                        <InputField
                          title="Trust/Corporation/Entity"
                          {...formProps.entityName}
                          name={`${formProps.entityName?.name}-${type}-${idx}`}
                          data-name={generateBeneficiaryTestID(
                            'BeneficiaryEntityName',
                            ENTITY_CATEGORY,
                            idx
                          )}
                          defaultValue={bene.entityName}
                          onChange={event =>
                            handleChange(event, idx, 'entityName')
                          }
                          className="blocked_data"
                        />
                      </div>
                      <div className="ssn-input">
                        <InputField
                          title="Tax ID Number"
                          placeholder="000-00-0000"
                          maxLength="11"
                          {...formProps.beneTaxID}
                          name={`${formProps.beneTaxID?.name}-${type}-${idx}`}
                          data-name={generateBeneficiaryTestID(
                            'BeneficiaryTaxId',
                            ENTITY_CATEGORY,
                            idx
                          )}
                          defaultValue={formatSsnForDisplay(bene.taxID)}
                          value={withDefault(
                            entitySSNValues[
                              `${formProps.beneTaxID?.name}-${type}-${idx}`
                            ],
                            formatSsnForDisplay(bene.taxID)
                          )}
                          onChange={e => {
                            handleChange(e, idx, 'taxID');
                            handleSpecialInputFormat(
                              e,
                              'SSN',
                              setEntitySSNValues,
                              `${formProps.beneTaxID?.name}-${type}-${idx}`
                            );
                          }}
                          className="blocked_data"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              <MFBeaconButton
                onClick={() => removeBene(idx)}
                id="removeBeneBtn"
                data-name={generateBeneficiaryTestID(
                  'RemoveBeneficiaryButton',
                  type,
                  idx
                )}
                type="button"
                variant="tertiary"
                label="Remove beneficiary"
              />
            </div>
          );
          /* eslint-enable complexity */
        })}

        <MFBeaconButton
          onClick={() => addBene()}
          id={`add-${type.toLowerCase()}-bene`}
          data-name={generateBeneficiaryTestID('AddBeneficiaryButton', type)}
          type="button"
          disabled={disabled || benes.length === 4}
          variant="tertiary"
          className="beacon-button-margin-override"
          icon-align="left"
          label={`Add ${type.toLowerCase()} beneficiary`}>
          <img slot="icon" src={plusIcon} alt="plus icon" />
        </MFBeaconButton>
        {beneficiaryTotals()}
        <style jsx>{styles}</style>
      </>
    );
  };

  return (
    <div>
      <div className="title-wrapper">
        <Heading text="Would you like to add beneficiaries?" />
      </div>
      <div className="input-container">
        <div className="primary bene-container">
          <div className="bene-title">
            {' '}
            Primary Beneficiary{' '}
            <ToolTip
              id="primary-beneficiary-tooltip"
              popOutPlacement="top"
              description="Why are we asking? If a primary beneficiary dies before you, the percentages will be re-calculated proportionately among the surviving primary beneficiaries unless you instruct otherwise."
            />
          </div>
          {BeneficiaryList(
            primaryBenes,
            setPrimaryBenes,
            beneficiaryTypes.PRIMARY
          )}
        </div>

        <div className="bene-container">
          <div className="bene-title">
            {' '}
            Secondary Beneficiary{' '}
            <ToolTip
              id="secondary-beneficiary-tooltip"
              popOutPlacement="top"
              description="Why are we asking? Secondary beneficiaries inherit assets only if no primary beneficiary survive you."
            />
          </div>
          {BeneficiaryList(
            secondaryBenes,
            setSecondaryBenes,
            beneficiaryTypes.SECONDARY,
            !arePrimaryBenes
          )}
        </div>
      </div>
      <div className="legal-container">
        <p>
          <strong>Note:</strong> You can change your beneficiaries at any time.
          Your account registration will include the designation “TOD” for
          Transfer on Death. A TOD ownership is a simplified estate planning
          method to designate beneficiaries.
        </p>
        <p>
          Any changes you make here will replace prior beneficiary designations
          for all TOD accounts with identical ownership registrations. Non-TOD
          accounts or TOD accounts with different ownership will not change.
        </p>
      </div>
      <div className="button-container">
        <MFBeaconButton
          variant="primary"
          size="normal"
          id="beneficiariesContinue"
          data-name={generateGeneralTestID('Beneficiaries', 'ContinueButton')}
          className="continue-btn"
          disabled={getSubmitDisabled()}
          onClick={next(getBeneForSubmission())}
          label="Continue"
        />
        <MFBeaconButton
          variant="secondary"
          size="normal"
          id="beneficiariesSkip"
          className="beacon-button-margin-override"
          data-name={generateGeneralTestID('Beneficiaries', 'SkipButton')}
          onClick={next(getShowSkipModal(showSkipModal))}
          label="Skip this step"
        />
        <MFBeaconButton
          variant="secondary"
          size="normal"
          id="beneficariesBack"
          className="beacon-button-margin-override"
          data-name={generateGeneralTestID('Beneficiaries', 'BackButton')}
          onClick={back()}
          label="Back"
        />
      </div>
      <PhoneNumber />
      <style jsx>{styles}</style>
    </div>
  );
};

export default BeneficiariesScreen;
